import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { MapPin, Crosshair, Maximize2, Search, X } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
};

const defaultCenter = {
  lat: 32.0853,
  lng: 34.7818, // Tel Aviv default
};

const MapLocationPicker = ({ 
  stops = [], 
  selectedStopId = null,
  onStopLocationChange,
  onStopSelect,
  showAllStops = true,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState(null);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const mapRef = useRef(null);

  // Calculate map center based on stops
  const getMapCenter = useCallback(() => {
    const gpsStops = stops.filter(s => s.latitude && s.longitude && !s.is_intro);
    if (gpsStops.length === 0) return defaultCenter;
    
    const avgLat = gpsStops.reduce((sum, s) => sum + parseFloat(s.latitude), 0) / gpsStops.length;
    const avgLng = gpsStops.reduce((sum, s) => sum + parseFloat(s.longitude), 0) / gpsStops.length;
    
    return { lat: avgLat, lng: avgLng };
  }, [stops]);

  // Fit bounds to show all stops
  const fitBoundsToStops = useCallback(() => {
    if (!map) return;
    
    const gpsStops = stops.filter(s => s.latitude && s.longitude && !s.is_intro);
    if (gpsStops.length === 0) return;
    
    if (gpsStops.length === 1) {
      map.setCenter({ lat: parseFloat(gpsStops[0].latitude), lng: parseFloat(gpsStops[0].longitude) });
      map.setZoom(16);
      return;
    }
    
    const bounds = new window.google.maps.LatLngBounds();
    gpsStops.forEach(stop => {
      bounds.extend({ lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) });
    });
    map.fitBounds(bounds, { padding: 50 });
  }, [map, stops]);

  // Center on selected stop
  useEffect(() => {
    if (map && selectedStopId) {
      const selectedStop = stops.find(s => s.stop_id === selectedStopId);
      if (selectedStop && selectedStop.latitude && selectedStop.longitude) {
        map.panTo({ lat: parseFloat(selectedStop.latitude), lng: parseFloat(selectedStop.longitude) });
        map.setZoom(17);
      }
    }
  }, [map, selectedStopId, stops]);

  const onLoad = useCallback((map) => {
    setMap(map);
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Handle map click to set location for selected stop
  const handleMapClick = useCallback((e) => {
    if (!selectedStopId || !onStopLocationChange) return;
    
    const lat = e.latLng.lat().toFixed(6);
    const lng = e.latLng.lng().toFixed(6);
    
    onStopLocationChange(selectedStopId, lat, lng);
  }, [selectedStopId, onStopLocationChange]);

  // Handle marker drag end
  const handleMarkerDragEnd = useCallback((stopId, e) => {
    if (!onStopLocationChange) return;
    
    const lat = e.latLng.lat().toFixed(6);
    const lng = e.latLng.lng().toFixed(6);
    
    onStopLocationChange(stopId, lat, lng);
  }, [onStopLocationChange]);

  // Apply searched location to selected stop
  const applySearchResultToStop = useCallback(() => {
    if (searchResult && selectedStopId && onStopLocationChange) {
      onStopLocationChange(selectedStopId, searchResult.lat, searchResult.lng);
      setSearchResult(null);
      setSearchInput('');
    }
  }, [searchResult, selectedStopId, onStopLocationChange]);

  // Clear search result
  const clearSearchResult = useCallback(() => {
    setSearchResult(null);
    setSearchInput('');
  }, []);

  // Get marker icon based on stop type and selection state
  const getMarkerIcon = (stop, index, isSelected) => {
    const isIntro = stop.is_intro;
    const isDriving = stop.navigation_mode === 'driving';
    
    // Color coding
    let fillColor = '#10B981'; // Green for walking
    if (isIntro) fillColor = '#14B8A6'; // Teal for intro
    else if (isDriving) fillColor = '#3B82F6'; // Blue for driving
    
    if (isSelected) fillColor = '#EF4444'; // Red for selected
    
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: fillColor,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 3,
      scale: isSelected ? 14 : 10,
    };
  };

  // Custom numbered marker using SVG
  const getNumberedMarkerIcon = (stop, index, isSelected) => {
    const isIntro = stop.is_intro;
    const isDriving = stop.navigation_mode === 'driving';
    
    let bgColor = '%2310B981'; // Green for walking (URL encoded)
    if (isIntro) bgColor = '%2314B8A6'; // Teal for intro
    else if (isDriving) bgColor = '%233B82F6'; // Blue for driving
    if (isSelected) bgColor = '%23EF4444'; // Red for selected
    
    const size = isSelected ? 40 : 32;
    const fontSize = isSelected ? 16 : 14;
    
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="${bgColor}" stroke="white" stroke-width="3"/>
        <text x="${size/2}" y="${size/2 + fontSize/3}" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">${index + 1}</text>
      </svg>
    `;
    
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size/2, size/2),
    };
  };

  if (loadError) {
    return (
      <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 text-center">
        <MapPin className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-700 font-semibold">Failed to load Google Maps</p>
        <p className="text-red-600 text-sm mt-1">Please check your API key configuration</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-gray-100 rounded-xl p-6 text-center animate-pulse" style={{ height: '400px' }}>
        <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold">Loading map...</p>
      </div>
    );
  }

  return (
    <div className={`relative ${isExpanded ? 'fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl p-4' : ''}`}>
      {/* Map Controls Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-cyan-600" />
          <span className="font-bold text-gray-800">Location Picker</span>
          {selectedStopId && (
            <span className="text-sm bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
              Click map to set location
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fitBoundsToStops}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            title="Fit all stops"
          >
            <Crosshair className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            title={isExpanded ? "Minimize" : "Expand"}
          >
            <Maximize2 className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs flex-wrap">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-teal-500"></span>
          <span className="text-gray-600">Intro</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500"></span>
          <span className="text-gray-600">Walking</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-blue-500"></span>
          <span className="text-gray-600">Driving</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500"></span>
          <span className="text-gray-600">Selected</span>
        </div>
      </div>

      {/* Address Search Bar */}
      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
          <input
            type="text"
            placeholder="Search address (e.g., Rothschild 1, Tel Aviv) and press Enter"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchInput.trim()) {
                e.preventDefault();
                // Use Geocoding service
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ address: searchInput }, (results, status) => {
                  if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    const lat = location.lat().toFixed(6);
                    const lng = location.lng().toFixed(6);
                    
                    setSearchResult({
                      lat,
                      lng,
                      address: results[0].formatted_address,
                    });
                    
                    // Center map on the searched location
                    if (map) {
                      map.panTo({ lat: parseFloat(lat), lng: parseFloat(lng) });
                      map.setZoom(17);
                    }
                  } else {
                    alert('Address not found. Try a more specific address.');
                  }
                });
              }
            }}
            className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all text-sm"
            data-testid="address-search-input"
          />
          {searchInput && (
            <button
              onClick={() => { setSearchInput(''); setSearchResult(null); }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1 ml-1">Type an address and press Enter to search</p>
        
        {/* Search Result Action Panel */}
        {searchResult && (
          <div className="mt-2 bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-300 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="font-bold text-gray-800 text-sm">Found Location:</p>
                <p className="text-gray-600 text-sm mt-1">{searchResult.address}</p>
                <p className="text-xs text-gray-500 mt-1 font-mono">
                  {searchResult.lat}, {searchResult.lng}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {selectedStopId ? (
                  <button
                    onClick={applySearchResultToStop}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap"
                    data-testid="apply-location-btn"
                  >
                    Set as Stop Location
                  </button>
                ) : (
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    Select a stop first to apply this location
                  </p>
                )}
                <button
                  onClick={clearSearchResult}
                  className="text-gray-500 hover:text-gray-700 text-xs underline"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={{ 
          ...containerStyle, 
          height: isExpanded ? 'calc(100vh - 200px)' : '400px' 
        }}
        center={getMapCenter()}
        zoom={14}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          mapTypeId: 'roadmap',
        }}
      >
        {/* Render all stops as markers */}
        {showAllStops && stops.map((stop, index) => {
          // Skip stops without coordinates (except for showing them in a list)
          if (!stop.latitude || !stop.longitude) return null;
          
          const isSelected = stop.stop_id === selectedStopId;
          const position = { 
            lat: parseFloat(stop.latitude), 
            lng: parseFloat(stop.longitude) 
          };

          return (
            <React.Fragment key={stop.stop_id}>
              {/* Marker */}
              <Marker
                position={position}
                icon={getNumberedMarkerIcon(stop, index, isSelected)}
                draggable={true}
                onDragEnd={(e) => handleMarkerDragEnd(stop.stop_id, e)}
                onClick={() => {
                  onStopSelect && onStopSelect(stop.stop_id);
                  setActiveInfoWindow(stop.stop_id);
                }}
                zIndex={isSelected ? 1000 : index}
              />
              
              {/* Radius circle for GPS stops */}
              {!stop.is_intro && (
                <Circle
                  center={position}
                  radius={stop.radius_meters || 50}
                  options={{
                    fillColor: isSelected ? '#EF4444' : '#10B981',
                    fillOpacity: 0.15,
                    strokeColor: isSelected ? '#EF4444' : '#10B981',
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                  }}
                />
              )}
              
              {/* Info Window */}
              {activeInfoWindow === stop.stop_id && (
                <InfoWindow
                  position={position}
                  onCloseClick={() => setActiveInfoWindow(null)}
                >
                  <div className="p-2 min-w-[150px]">
                    <p className="font-bold text-gray-900 text-sm">{stop.title}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      {stop.is_intro && (
                        <span className="bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Intro</span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded ${
                        stop.navigation_mode === 'driving' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {stop.navigation_mode === 'driving' ? 'Driving' : 'Walking'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {parseFloat(stop.latitude).toFixed(6)}, {parseFloat(stop.longitude).toFixed(6)}
                    </p>
                    {!stop.is_intro && (
                      <p className="text-xs text-gray-500">Radius: {stop.radius_meters || 50}m</p>
                    )}
                  </div>
                </InfoWindow>
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>

      {/* Instructions */}
      <div className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
        <p className="font-semibold text-gray-700 mb-1">How to use:</p>
        <ul className="space-y-1 text-xs">
          <li>• <strong>Click a marker</strong> to select and view stop details</li>
          <li>• <strong>Drag a marker</strong> to reposition the stop</li>
          <li>• <strong>Click on the map</strong> to set the location for the selected stop (shown in red)</li>
          <li>• The colored circles show the trigger radius for each stop</li>
        </ul>
      </div>

      {/* Expanded overlay close button */}
      {isExpanded && (
        <button
          onClick={() => setIsExpanded(false)}
          className="absolute top-2 right-2 bg-white shadow-lg rounded-full p-2 hover:bg-gray-100"
        >
          <span className="text-gray-600 font-bold">✕</span>
        </button>
      )}
    </div>
  );
};

export default MapLocationPicker;
